import { useCurrentAccountStore } from "@/stores/currentAccount"
import { mapQueryResult } from "@/api/utils/mapQuery"
import { IItemsByDate } from "@/models/activityFeed/ActivityFeedItem"
import { INote } from "@/models/note/note.types"
import { formatDate } from "@/helpers/dateHelper"

export const useDeceasedHelper = () => {
  const current = useCurrentAccountStore()

  const isMeterRemindersDisabled = () =>
    mapQueryResult(current.account, () => {
      return (
        !current.meterReminders.data.length ||
        current.meterReminders.data.every(
          (reminder) => reminder.active === false,
        )
      )
    })

  const isCommsDisabled = () =>
    mapQueryResult(current.accountContacts, (accountContacts) => {
      const primary = accountContacts.data.find(
        (contact) => contact.juniferPrimaryContact,
      )

      return (
        !primary?.receiveEmail &&
        !primary?.receivePost &&
        !primary?.receiveSMS &&
        primary?.billDelivery === "None"
      )
    })

  const isAccountInReview = () =>
    mapQueryResult(current.underReviewPeriods, (reviewPeriods) => {
      const isDeceased = reviewPeriods.data.some(
        (reviewPeriod) =>
          reviewPeriod.isDeceased && reviewPeriod.isDunningSuppressed,
      )

      return isDeceased
    })

  const isTheEstateOf = () =>
    mapQueryResult(
      current.primaryAccountContact,
      (query) => query.data?.juniferPrimaryContact && query.data?.isDeceased,
    )

  const areAllCommsPaused = () =>
    mapQueryResult(isMeterRemindersDisabled(), () => {
      return (
        isMeterRemindersDisabled().data &&
        isCommsDisabled().data &&
        isAccountInReview().data
      )
    })

  const flattenNotes = (feedItems: IItemsByDate[]) => {
    return feedItems.flatMap((entry) => entry.items.map((item) => item.item))
  }

  const isActivityStreamNotified = () =>
    mapQueryResult(current.activityFeedEvents, (activityFeedEvents) => {
      const notesFlattened = flattenNotes(activityFeedEvents.data)

      return notesFlattened.some((note) =>
        (note as INote).summary?.includes("Bereavement details"),
      )
    })

  const bereavementDetails = () =>
    mapQueryResult(current.activityFeedEvents, (activityFeedEvents) => {
      const notesFlattened = flattenNotes(activityFeedEvents.data)

      const foundNote = notesFlattened.find((note) =>
        (note as INote).summary?.includes("Bereavement details"),
      ) as INote

      return foundNote
        ? `Created: ${formatDate(foundNote.createdDate)}, Summary: ${
            foundNote.summary
          }${foundNote.content ? `, Details: ${foundNote.content}` : ""}`
        : ""
    })

  const hasSubmittedDeathCertTicketNumber = () =>
    mapQueryResult(current.activityFeedEvents, (activityFeedEvents) => {
      const notesFlattened = flattenNotes(activityFeedEvents.data)

      return notesFlattened.some((note) =>
        (note as INote).summary?.includes("Death Certificate Received"),
      )
    })

  const deathCertTicketNumberDetails = () =>
    mapQueryResult(current.activityFeedEvents, (activityFeedEvents) => {
      const notesFlattened = flattenNotes(activityFeedEvents.data)

      const foundNote = notesFlattened.find((note) =>
        (note as INote).summary?.includes("Death Certificate Received"),
      ) as INote

      return foundNote
        ? `Submitted: ${formatDate(foundNote.createdDate)}, ${
            foundNote.content
          }`
        : ""
    })

  const hasSecondaryContacts = () =>
    mapQueryResult(current.accountContacts, (contacts) => {
      return contacts.data.some((contact) => !contact.juniferPrimaryContact)
    })

  const noSecondaryContacts = () =>
    mapQueryResult(hasSecondaryContacts(), () => {
      return !hasSecondaryContacts().data
    })

  return {
    isMeterRemindersDisabled,
    isCommsDisabled,
    isAccountInReview,
    isTheEstateOf,
    areAllCommsPaused,
    isActivityStreamNotified,
    bereavementDetails,
    hasSubmittedDeathCertTicketNumber,
    deathCertTicketNumberDetails,
    hasSecondaryContacts,
    noSecondaryContacts,
  }
}
